import React, { FC } from "react";
import { Link } from "react-router-dom";
import moment from "moment";
import { MapPinIcon } from "@heroicons/react/24/outline";
import { useSelector } from "react-redux";
import { RootState } from "redux/store";
import { currencySymbol } from "shared/CurrencySymbol";

export interface BookingCardProps {
  className?: string;
  booking: any;
}

const BookingCard: FC<BookingCardProps> = ({ className = "", booking }) => {
  const { currentUser } = useSelector((state: RootState) => state.user);
  const property = booking?.property;
  const href = `/booking_request?id=${booking?.id}`;

  const isShared =
    booking?.is_shared_property === true ||
    booking?.is_shared_property === "true";

  const firstImage: string | null =
    Array.isArray(property?.place_images) && property.place_images.length > 0
      ? property.place_images[0]
      : null;

  const formatDate = (d: string) =>
    moment(d, "MMMM DD, YYYY").isValid()
      ? moment(d, "MMMM DD, YYYY").format("MMM DD, YYYY")
      : d;

  const getDuration = () => {
    if (booking?.no_of_months > 0)
      return `${booking.no_of_months} month${booking.no_of_months !== 1 ? "s" : ""}`;
    const nights = moment(booking?.check_out_date, "MMMM DD, YYYY").diff(
      moment(booking?.check_in_date, "MMMM DD, YYYY"),
      "days"
    );
    return `${nights} night${nights !== 1 ? "s" : ""}`;
  };

  const paidAt: string | null =
    booking?.invoice?.paid_at || booking?.paid_at || null;

  const initials = (name: string) =>
    name.split(" ").map((n: string) => n[0]).join("").slice(0, 2).toUpperCase();

  return (
    <Link
      to={href}
      className={`nc-BookingCard group flex flex-col sm:flex-row bg-white dark:bg-neutral-900 border border-neutral-100 dark:border-neutral-800 rounded-2xl overflow-hidden hover:shadow-lg transition-shadow ${className}`}
      style={{ textDecoration: "none" }}
    >
      {/* ── IMAGE ── */}
      {/* Mobile: full width, fixed height. Desktop: fixed width, full height */}
      <div className="relative w-full sm:w-44 flex-shrink-0" style={{ minHeight: "200px" }}>
        {firstImage ? (
          <img
            src={firstImage}
            alt={property?.title || "Property"}
            className="absolute inset-0 w-full h-full object-cover"
          />
        ) : (
          <div className="absolute inset-0 flex items-center justify-center bg-neutral-200 dark:bg-neutral-700">
            <MapPinIcon className="w-10 h-10 text-neutral-400" />
          </div>
        )}

        {/* Confirmed ribbon */}
        <span
          className="absolute top-3 left-0 flex items-center gap-1 px-2 py-1 text-white font-medium"
          style={{
            fontSize: "10px",
            background: "#0F6E56",
            borderRadius: "0 6px 6px 0",
            letterSpacing: "0.05em",
          }}
        >
          <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="20 6 9 17 4 12" />
          </svg>
          CONFIRMED
        </span>

        {/* Shared / Entire pill */}
        <span
          className="absolute bottom-2 left-2 text-white rounded-md px-2 py-0.5"
          style={{ fontSize: "10px", background: "rgba(0,0,0,0.52)" }}
        >
          {isShared ? "Shared" : "Entire"}
        </span>
      </div>

      {/* ── CONTENT ── */}
      <div className="flex flex-col flex-1 min-w-0 p-4 gap-3">

        {/* ROW 1 — title + amount */}
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0 flex-1">
            <p className="font-medium text-sm text-neutral-900 dark:text-neutral-100 truncate leading-snug mb-0.5">
              {property?.title}
            </p>
            <div className="flex items-center gap-1">
              <MapPinIcon className="w-3 h-3 flex-shrink-0 text-neutral-400" />
              <span className="text-xs text-neutral-400 dark:text-neutral-500 truncate">
                {property?.address}
              </span>
            </div>
          </div>
          <div className="text-right flex-shrink-0">
            <p className="text-base font-medium text-neutral-900 dark:text-neutral-100 leading-tight">
              {currencySymbol()} {Number(booking?.total_amount)?.toFixed(2)}
            </p>
            <span
              className="text-xs font-medium px-2 py-0.5 rounded-full inline-block mt-0.5"
              style={{ background: "#EAF3DE", color: "#27500A" }}
            >
              Paid
            </span>
          </div>
        </div>

        {/* DIVIDER */}
        <hr className="border-neutral-100 dark:border-neutral-800 m-0" />

        {/* ROW 2 — date timeline */}
        <div className="flex items-center">
          <div>
            <p className="text-neutral-400 uppercase m-0" style={{ fontSize: "9px", letterSpacing: "0.07em" }}>
              Check-in
            </p>
            <p className="text-sm font-medium text-neutral-800 dark:text-neutral-200 m-0">
              {formatDate(booking?.check_in_date)}
            </p>
          </div>

          <div className="flex flex-col items-center flex-1 px-3">
            <div className="relative w-full" style={{ height: "14px" }}>
              <div
                className="absolute top-1/2 w-full"
                style={{ height: "1px", background: "#d1d5db", transform: "translateY(-50%)" }}
              />
              <div
                className="absolute rounded-full"
                style={{ width: "6px", height: "6px", background: "#1D9E75", top: "50%", left: 0, transform: "translateY(-50%)" }}
              />
              <div
                className="absolute rounded-full"
                style={{ width: "6px", height: "6px", background: "#1D9E75", top: "50%", right: 0, transform: "translateY(-50%)" }}
              />
            </div>
            <p className="text-xs text-neutral-400 m-0 mt-1">{getDuration()}</p>
          </div>

          <div className="text-right">
            <p className="text-neutral-400 uppercase m-0" style={{ fontSize: "9px", letterSpacing: "0.07em" }}>
              Check-out
            </p>
            <p className="text-sm font-medium text-neutral-800 dark:text-neutral-200 m-0">
              {formatDate(booking?.check_out_date)}
            </p>
          </div>
        </div>

        {/* DIVIDER */}
        <hr className="border-neutral-100 dark:border-neutral-800 m-0" />

        {/* ROW 3 — room highlight (shared only) */}
        {isShared && booking?.booked_room && (
          <div
            className="flex items-center gap-2 px-3 py-1.5 rounded-lg"
            style={{ background: "#E6F1FB" }}
          >
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#185FA5" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/>
            </svg>
            <span className="text-xs font-medium" style={{ color: "#185FA5" }}>
              {booking.booked_room}
            </span>
          </div>
        )}

        {/* ROW 4 — meta strip */}
        <div className="flex items-center justify-between flex-wrap gap-y-2">
          <div className="flex items-center gap-3 flex-wrap">
            <span className="text-xs text-neutral-500 dark:text-neutral-400">
              {booking?.number_of_guests} guest{booking?.number_of_guests !== 1 ? "s" : ""}
            </span>

            {!isShared && property?.rooms_count > 0 && (
              <span className="text-xs text-neutral-500 dark:text-neutral-400">
                {property.rooms_count} room{property.rooms_count !== 1 ? "s" : ""}
              </span>
            )}

            {currentUser?.is_host && booking?.request_by && (
              <span className="flex items-center gap-1.5">
                <span
                  className="inline-flex items-center justify-center rounded-full font-medium flex-shrink-0"
                  style={{ width: "20px", height: "20px", fontSize: "9px", background: "#E6F1FB", color: "#185FA5" }}
                >
                  {initials(booking.request_by)}
                </span>
                <span className="text-xs text-neutral-600 dark:text-neutral-300">
                  {booking.request_by}
                </span>
              </span>
            )}
          </div>

          {/* ref + invoice — on mobile these wrap to their own row */}
          <div className="flex items-center gap-2 flex-wrap">
            {booking?.invoice_no && (
              <span
                className="text-xs px-2 py-0.5 rounded"
                style={{ background: "#E6F1FB", color: "#185FA5" }}
              >
                INV-{booking.invoice_no}
              </span>
            )}
            {booking?.number && (
              <span className="text-xs text-neutral-400 dark:text-neutral-500">
                #{booking.number}
              </span>
            )}
          </div>
        </div>

        {/* ROW 5 — paid date */}
        {paidAt && (
          <div
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg"
            style={{ background: "#EAF3DE" }}
          >
            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="#27500A" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
            </svg>
            <span className="text-xs" style={{ color: "#27500A" }}>
              Payment received: {paidAt}
            </span>
          </div>
        )}

      </div>
    </Link>
  );
};

export default BookingCard;